import {Input} from '@cashiaApp/web-components';
import React from 'react';

import EyeSlash from '../assets/icons/eye-slash.svg';
import Eye from '../assets/icons/eye.svg';

interface CredentialsFieldsProps {
  keyId: string;
  secretKey: string;
  terminalId: string;
  showPassword: boolean;
  onTogglePassword: () => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  renderErrorMessage: (fieldName: string) => React.ReactNode;
}

/**
 * CredentialsFields Component
 *
 * Reusable component for Cashia API authentication credentials:
 * - Terminal ID
 * - Key ID (masked password field)
 * - Secret Key (masked password field)
 *
 * Includes password visibility toggle for Key ID and Secret Key fields.
 *
 * @example
 * ```tsx
 * <CredentialsFields
 *   keyId={values.keyId}
 *   secretKey={values.secretKey}
 *   terminalId={values.terminalId}
 *   showPassword={showPassword}
 *   onTogglePassword={() => setShowPassword(!showPassword)}
 *   onChange={handleChange}
 *   renderErrorMessage={renderErrorMessage}
 * />
 * ```
 */
export const CredentialsFields: React.FC<CredentialsFieldsProps> = ({
  keyId,
  secretKey,
  terminalId,
  showPassword,
  onTogglePassword,
  onChange,
  renderErrorMessage,
}) => {
  return (
    <>
      {/* Terminal ID Field */}
      <div className="my-1">
        <label className="text-darkGrey text-sm font-bold mb-1 flex gap-2 items-center">
          TerminalId * {renderErrorMessage('terminalId')}
        </label>
        <Input
          name="terminalId"
          value={terminalId}
          onChange={onChange}
          placeholder="Enter terminal ID"
          className="w-full bg-transparent rounded-lg h-12 focus:ring-2 focus-visible:border-none focus:ring-[#FECED8]"
        />
      </div>

      {/* Key ID Field with Password Toggle */}
      <div className="flex items-center mt-2 mb-1 gap-2">
        <p className="text-darkGrey text-sm font-bold font-['Metropolis']">
          KeyId *
        </p>
        <p className="flex-1">{renderErrorMessage('keyId')}</p>
      </div>
      <div className="w-full flex justify-center items-center bg-transparent rounded-lg h-12 focus:ring-2 focus:ring-[#FECED8] relative">
        <Input
          name="keyId"
          value={keyId}
          onChange={onChange}
          type={showPassword ? 'text' : 'password'}
          placeholder="Enter API key ID"
          className="w-full bg-transparent rounded-lg h-12 focus:ring-2 focus-visible:border-none focus:ring-[#FECED8]"
        />
        <div
          onClick={onTogglePassword}
          className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer"
          role="button"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onTogglePassword();
            }
          }}>
          {showPassword ? (
            <img src={Eye} height={16} width={16} alt="Show" />
          ) : (
            <img src={EyeSlash} height={16} width={16} alt="Hide" />
          )}
        </div>
      </div>

      {/* Secret Key Field with Password Toggle */}
      <div className="flex items-center mt-2 mb-1 gap-2">
        <p className="text-darkGrey text-sm font-bold font-['Metropolis']">
          SecretKey *
        </p>
        <p className="flex-1">{renderErrorMessage('secretKey')}</p>
      </div>
      <div className="w-full flex justify-center items-center bg-transparent rounded-lg h-12 focus:ring-2 focus:ring-[#FECED8] relative">
        <Input
          name="secretKey"
          value={secretKey}
          onChange={onChange}
          type={showPassword ? 'text' : 'password'}
          placeholder="Enter secret key"
          className="w-full bg-transparent rounded-lg h-12 focus:ring-2 focus-visible:border-none focus:ring-[#FECED8]"
        />
        <div
          onClick={onTogglePassword}
          className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer"
          role="button"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onTogglePassword();
            }
          }}>
          {showPassword ? (
            <img src={Eye} height={16} width={16} alt="Show" />
          ) : (
            <img src={EyeSlash} height={16} width={16} alt="Hide" />
          )}
        </div>
      </div>
    </>
  );
};
